import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import Tooltip from '@mui/material/Tooltip';
import { SPRITE_URL } from '@zarya/theme';
import type { MapNode } from './AccessMap.api';
import { groupEdgesByPair, groupLabel, groupLines, pairKey } from './edgeGroups';
import {
  LABEL_LIMIT,
  SAME_COLUMN,
  NODE_R,
  RECUO_DO_LACO,
  LABEL_DY,
  SUB_DY,
  type EdgePosicionada,
  type GraphLayout,
  type NoPosicionado,
  type Point,
} from './useGraphLayout';
import { areaDoDesenho, caberNaArea, ZOOM_MAX, ZOOM_MIN } from './caberNaArea';
import { desesticar, esticamentoFor, esticarLayout } from './esticarVertical';
import {
  AM_TYPE,
  typeLabel,
  edgeLabel,
  inSubtitle,
  edgeText,
  limitsText,
  inKind,
  outOfReach,
  reachText,
  truncar,
} from './accessMapVocab';
import s from './AccessMap.module.css';
import { t } from '@app/i18n';

/**
 * O grafo de T19 em SVG próprio (FR-102) — sem biblioteca nova, como manda o
 * `CLAUDE.md` do repo.
 *
 * Interações: arrastar o fundo move (pan), roda e duplo-clique dão zoom (0,3× a
 * 4×), arrastar um nó o reposiciona, passar o mouse realça o caminho até ele
 * quando não há seleção, clicar seleciona, e clicar no fundo ou Esc limpa.
 *
 * O zoom **não** leva nada junto: a roda cancela o evento (listener nativo não
 * passivo, senão a página rola) e o duplo-clique não mexe na seleção — os dois
 * `click` do gesto ficariam alternando o nó para fora (T386, Bug 3).
 *
 * O arrasto é por **eventos de mouse**, não de ponteiro: o `PointerEvent` não
 * existe no jsdom, e sem ele o arrasto viraria a única interação da tela sem
 * teste automatizado. No navegador, toque e caneta chegam como mouse emulado.
 *
 * Sem os botões `+ / ▾` do protótipo (corte do §4 do PRD). O `Enquadrar`, que
 * também estava cortado, voltou em 29/08 como **Ajustar à tela** no cabeçalho da
 * tela: com o card de altura fixa, um mapa mal enquadrado não tem mais como ser
 * consertado rolando a página. Aqui ele chega como `ajusteSolicitado`.
 *
 * Cada nó é um `role="button"` alcançável por Tab, com Enter/Espaço
 * selecionando: no protótipo o grafo é `role="img"` e só responde ao mouse, o
 * que deixaria a tela inteira fora do teclado. É divergência intencional
 * (registrada em `docs/progress/us11.md`) — e é ela que dá ao E2E um papel
 * estável por nó, além dos `data-*`.
 *
 * RÓTULO POR PAR, NÃO POR ARESTA (05/09). O servidor emite uma aresta `tool`
 * por capability; três tools do mesmo MCP são três curvas idênticas com três
 * rótulos no mesmo ponto — ilegíveis. As curvas ficam (cada aresta segue no DOM
 * com os seus `data-*`), o rótulo vira um por par: «faturas.consultar +2», e o
 * tooltip da casa lista todas, uma por linha (`edgeGroups.ts`). Passar o mouse
 * sobre a aresta realça o caminho como passar sobre o nó de origem — é o que
 * deixa o rótulo à mão para o tooltip.
 */

export const DICA = () => t('accessMap.graph.hint');

export interface GraphProps {
  layout: GraphLayout;
  selecionado: string | null;
  onSelecionar: (id: string | null) => void;
  onMover: (id: string, ponto: Point) => void;
  /** Realce por nome (FR-102): quem não casa recebe `fade`. */
  busca: string;
  /** **Focar ao selecionar**: reduz o grafo à vizinhança do nó selecionado. */
  focar: boolean;
  /**
   * Contador de **Ajustar à tela**: cada incremento refaz o enquadramento e
   * devolve o controle ao automático. É contador, e não booleano, porque o
   * pedido é um evento — clicar duas vezes seguidas tem de enquadrar duas vezes.
   */
  ajusteSolicitado?: number;
}

type Arrasto =
  | { tipo: 'fundo'; x: number; y: number }
  | { tipo: 'no'; id: string; x: number; y: number; origem: Point };

function casa(no: MapNode, search: string): boolean {
  if (!search) return true;
  return no.label.toLowerCase().includes(search.toLowerCase());
}

/**
 * Traçado da aresta, como o `amEdgeGeom` do protótipo: sai e chega na **borda**
 * do disco (`x ± NODE_R`), com controles proporcionais ao desnível — quanto
 * mais alta a diferença de linha, mais aberta a curva, que é o que evita o
 * traço reto passando por cima dos nós do meio. Devolve também o meio da curva
 * (Bézier em t = ½), onde o rótulo é escrito.
 */
function edgeGeometria(de: Point, para: Point): { d: string; mx: number; my: number } {
  const my = (de.y + para.y) / 2;
  // Mesma coluna (A2A entre identidades): laço pela esquerda, senão a curva
  // vira uma reta vertical em cima da própria coluna.
  if (Math.abs(de.x - para.x) < SAME_COLUMN) {
    const cx = Math.min(de.x, para.x) - RECUO_DO_LACO;
    return {
      d: `M${de.x - NODE_R},${de.y} C${cx},${de.y} ${cx},${para.y} ${para.x - NODE_R},${para.y}`,
      mx: cx + 20,
      my,
    };
  }
  const forDireita = de.x < para.x;
  const sx = forDireita ? de.x + NODE_R : de.x - NODE_R;
  const tx = forDireita ? para.x - NODE_R : para.x + NODE_R;
  const opening = 60 + 60 * Math.min(1, Math.abs(para.y - de.y) / 400);
  const c1 = forDireita ? sx + opening : sx - opening;
  const c2 = forDireita ? tx - opening : tx + opening;
  return {
    d: `M${sx},${de.y} C${c1},${de.y} ${c2},${para.y} ${tx},${para.y}`,
    mx: 0.125 * sx + 0.375 * c1 + 0.375 * c2 + 0.125 * tx,
    my,
  };
}

export function Graph({
  layout,
  selecionado,
  onSelecionar,
  onMover,
  busca,
  focar,
  ajusteSolicitado = 0,
}: GraphProps) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [hover, setHover] = useState<string | null>(null);
  const arrasto = useRef<Arrasto | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  /** Caixa do SVG em pixels de tela — metade da conta do enquadramento. */
  const [caixa, setCaixa] = useState<{ largura: number; altura: number } | null>(null);
  /**
   * O usuário já mexeu no enquadramento (roda, duplo-clique, arraste)? A partir
   * daí o automático se cala: reenquadrar por baixo de quem acabou de aproximar
   * um nó é pior do que ficar mal enquadrado. «Ajustar à tela» é o que devolve.
   */
  const manual = useRef(false);
  const lastSetting = useRef(ajusteSolicitado);

  /**
   * O desenho é o layout com o `y` esticado para a proporção da seção — é ele
   * que se enquadra, se mede e se pinta. O `layout` cru continua sendo o espaço
   * em que as posições arrastadas são gravadas (ver `esticarVertical.ts`).
   */
  const esticamento = useMemo(() => esticamentoFor(layout, caixa), [layout, caixa]);
  const desenho = useMemo(() => esticarLayout(layout, esticamento), [layout, esticamento]);

  const vbWidth = Math.max(desenho.largura, 640);
  const vbHeight = Math.max(desenho.altura, 360);

  /**
   * A seção muda de tamanho por conta própria — a janela, a aba, o empilhamento
   * abaixo de 1000 px. `ResizeObserver` não existe no jsdom: sem ele `caixa`
   * fica nula, `caberNaArea` devolve a identidade e o grafo abre em 1× — que é
   * como os testes de `AccessMapPage` o veem.
   */
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || typeof ResizeObserver === 'undefined') return undefined;
    const observador = new ResizeObserver((inputs) => {
      const medida = inputs[0]?.contentRect;
      if (!medida) return;
      setCaixa((current) =>
        current &&
        Math.abs(current.largura - medida.width) < 1 &&
        Math.abs(current.altura - medida.height) < 1
          ? current
          : { largura: medida.width, altura: medida.height },
      );
    });
    observador.observe(svg);
    return () => observador.disconnect();
  }, []);

  const enquadrar = useCallback(() => {
    if (!caixa) return;
    const alvo = areaDoDesenho(desenho);
    if (!alvo) return;
    const setting = caberNaArea(alvo, { largura: vbWidth, altura: vbHeight }, caixa);
    setZoom(setting.zoom);
    setPan(setting.pan);
  }, [caixa, desenho, vbHeight, vbWidth]);

  /**
   * Enquadra na montagem, quando a seção muda de tamanho e quando os dados
   * mudam — e refaz, por cima do `manual`, a cada pedido de «Ajustar à tela».
   */
  useEffect(() => {
    if (lastSetting.current !== ajusteSolicitado) {
      lastSetting.current = ajusteSolicitado;
      manual.current = false;
    }
    if (manual.current) return;
    enquadrar();
  }, [ajusteSolicitado, enquadrar]);

  /**
   * A roda dá zoom **e nada mais**: sem `preventDefault` a página rola junto e
   * o mapa sai de debaixo do cursor (T386, Bug 3a). O listener é **nativo e não
   * passivo** — o `onWheel` do React entra na raiz como passivo (React 17+), e
   * `preventDefault` dentro dele é ignorado pelo navegador.
   */
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    function aoRolar(event: WheelEvent) {
      event.preventDefault();
      manual.current = true;
      const fator = event.deltaY < 0 ? 1.1 : 1 / 1.1;
      setZoom((current) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, current * fator)));
    }
    svg.addEventListener('wheel', aoRolar, { passive: false });
    return () => svg.removeEventListener('wheel', aoRolar);
  }, []);

  // Com **Focar ao selecionar** ligado, o grafo se reduz à vizinhança do nó.
  const visiveis =
    focar && selecionado ? new Set([selecionado, ...desenho.vizinhanca(selecionado)]) : null;

  const noVisivel = (item: NoPosicionado) => !visiveis || visiveis.has(item.no.id);
  const edgeVisivel = (item: EdgePosicionada) =>
    !visiveis || (visiveis.has(item.aresta.from) && visiveis.has(item.aresta.to));

  // Realce: o selecionado manda; sem seleção, quem está sob o mouse.
  const emFoco = selecionado ?? hover;
  const realcados = emFoco ? new Set([emFoco, ...desenho.vizinhanca(emFoco)]) : null;

  /**
   * Sob a lente, o que a pessoa NÃO alcança esmaece — e esmaecer, nunca remover:
   * o caminho existe para a identidade, e apagá-lo diria que ele não existe.
   *
   * O conjunto sai das próprias arestas anotadas: um nó está no alcance quando
   * ao menos uma aresta alcançada o toca. Recalcular o alcance aqui seria a
   * terceira leitura da mesma decisão — a primeira é o PDP, a segunda o
   * servidor.
   */
  const noAlcance = useMemo(() => {
    const anotadas = desenho.arestas.filter((item) => item.aresta.reach != null);
    if (anotadas.length === 0) return null;
    const dentro = new Set<string>();
    for (const item of anotadas) {
      if (!item.aresta.reach?.reached) continue;
      dentro.add(item.aresta.from);
      dentro.add(item.aresta.to);
    }
    return dentro;
  }, [desenho.arestas]);

  function apagado(id: string, no?: MapNode): boolean {
    if (no && !casa(no, busca)) return true;
    if (realcados) return !realcados.has(id);
    if (noAlcance) return !noAlcance.has(id);
    return false;
  }

  function aoMover(event: ReactMouseEvent<SVGSVGElement>) {
    const current = arrasto.current;
    if (!current) return;
    // Aqui, e não no `mousedown`: um clique sem arrastar não é ajuste manual, e
    // marcá-lo calaria o reenquadramento por um clique de seleção.
    manual.current = true;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (current.tipo === 'fundo') {
      setPan((previous) => ({ x: previous.x + dx, y: previous.y + dy }));
      arrasto.current = { ...current, x: event.clientX, y: event.clientY };
      return;
    }
    // O deslocamento em pixels da tela vira deslocamento no grafo pelo zoom — e
    // o `y` volta ao espaço do layout antes de ser gravado, senão a posição
    // salva numa janela larga reapareceria deslocada numa estreita.
    onMover(current.id, {
      x: current.origem.x + (event.clientX - current.x) / zoom,
      y: desesticar(current.origem.y + (event.clientY - current.y) / zoom, esticamento),
    });
  }

  function closeArrasto() {
    arrasto.current = null;
  }

  const visibleEdges = desenho.arestas.filter(edgeVisivel);
  const groups = groupEdgesByPair(visibleEdges);

  return (
    <svg
      ref={svgRef}
      className={s.svg}
      // `group`, não `img`: com nós focáveis dentro, `img` tornaria o conteúdo
      // apresentacional e o leitor de tela não anunciaria nenhum deles.
      role="group"
      aria-label={t('accessMap.graph.ariaFull')}
      data-testid="access-map-svg"
      data-zoom={zoom.toFixed(2)}
      viewBox={`0 0 ${vbWidth} ${vbHeight}`}
      preserveAspectRatio="xMidYMid meet"
      onMouseDown={(event) => {
        // Só o fundo: o nó chama `stopPropagation`.
        arrasto.current = { tipo: 'fundo', x: event.clientX, y: event.clientY };
      }}
      onMouseMove={aoMover}
      onMouseUp={closeArrasto}
      onMouseLeave={closeArrasto}
      onDoubleClick={() => {
        manual.current = true;
        setZoom((current) => Math.min(ZOOM_MAX, current * 1.4));
      }}
      onClick={(event) => {
        // O segundo clique de um duplo-clique é zoom, não limpeza (Bug 3b):
        // limpar ali tiraria a seleção no meio do gesto de aproximar.
        if (event.detail > 1) return;
        onSelecionar(null);
      }}
    >
      {/* Seta do protótipo: `currentColor` deixa o marcador herdar a cor da
          aresta (contrato cinza, política laranja, A2A verde). */}
      <defs>
        <marker
          id="am-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="8"
          markerHeight="8"
          orient="auto"
        >
          <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
        </marker>
      </defs>
      <g
        data-testid="access-map-mundo"
        data-pan={`${Math.round(pan.x)},${Math.round(pan.y)}`}
        transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}
      >
        {desenho.colunas.map((coluna) => (
          <text
            key={coluna.tipo}
            className={s.colunaRotulo}
            x={coluna.x}
            y={34}
            textAnchor="middle"
          >
            {t(coluna.rotuloKey)}
          </text>
        ))}

        {visibleEdges.map((item) => {
          const geo = edgeGeometria(item.de, item.para);
          // O par desta aresta: quem rotula é a primeira do par, e só ela.
          const group = groups.get(pairKey(item)) ?? [item];
          const leader = group[0].chave === item.chave;
          const grouped = group.length > 1;
          // Fora do alcance da lente a aresta esmaece por si — mesmo que as duas
          // pontas estejam no alcance por OUTRO caminho. É o caso do §9: a
          // pessoa chega ao resource pela política e não pelo contrato.
          const fraco =
            outOfReach(item.aresta) || (apagado(item.aresta.from) && apagado(item.aresta.to));
          // Realçada = ponta ligada ao nó em foco. É o que traz o rótulo à
          // tona ("passe o mouse para ver os rótulos", §2.2).
          const realcada =
            emFoco !== null && (item.aresta.from === emFoco || item.aresta.to === emFoco);
          return (
            <g
              key={item.chave}
              className={[
                s.aresta,
                s[`aresta_${item.aresta.kind}`],
                realcada ? s.arestaOn : '',
                fraco ? s.fade : '',
              ]
                .filter(Boolean)
                .join(' ')}
              data-testid={`aresta-${item.aresta.kind}`}
              data-origem={item.aresta.origin ?? undefined}
              data-de={item.aresta.from}
              data-para={item.aresta.to}
              data-rotulo={item.aresta.label ?? undefined}
              data-limites={limitsText(item.aresta) || undefined}
              // Ganchos da lente para o E2E e para o teste de componente: o
              // alcance e o diagnóstico por eixo, sem depender do `<title>`.
              data-reach={
                item.aresta.reach == null ? undefined : item.aresta.reach.reached ? 'sim' : 'nao'
              }
              data-axes={reachText(item.aresta) || undefined}
              // Sob o mouse a aresta realça o caminho da origem, como o nó faz:
              // é o que mantém o rótulo visível enquanto o ponteiro vai até ele.
              onMouseEnter={() => setHover(item.aresta.from)}
              onMouseLeave={() =>
                setHover((current) => (current === item.aresta.from ? null : current))
              }
            >
              <title>{edgeText(item.aresta)}</title>
              <path d={geo.d} markerEnd="url(#am-arrow)" />
              {/* O rótulo só aparece na aresta realçada (protótipo: `.amlbl`
                  é `display:none` fora de `.hi`) — desenhar os nove de uma vez
                  é o que embaralhava o mapa. O texto inteiro fica no `<title>`. */}
              {leader && grouped ? (
                // Par com mais de uma tool: UM rótulo («a primeira +N») que recebe
                // mouse e foco, e o tooltip da casa lista todas, uma por linha.
                <Tooltip
                  title={
                    <span className={s.arestaTooltip}>
                      {groupLines(group).map((line) => (
                        <span key={line}>{line}</span>
                      ))}
                    </span>
                  }
                  arrow
                  describeChild
                  enterTouchDelay={0}
                >
                  <text
                    className={`${s.arestaRotulo} ${s.arestaRotuloGrupo}`}
                    x={geo.mx}
                    y={geo.my - 6}
                    textAnchor="middle"
                    tabIndex={0}
                    data-testid="rotulo-aresta-grupo"
                    data-tools={group.length}
                  >
                    {truncar(groupLabel(group), 30)}
                  </text>
                </Tooltip>
              ) : leader ? (
                <text className={s.arestaRotulo} x={geo.mx} y={geo.my - 6} textAnchor="middle">
                  {truncar(edgeLabel(item.aresta), 30)}
                </text>
              ) : null}
            </g>
          );
        })}

        {desenho.nos.filter(noVisivel).map((item) => {
          const kindDoNo = inKind(item.no);
          const tipo = AM_TYPE[kindDoNo];
          const ativo = selecionado === item.no.id;
          return (
            <g
              key={item.no.id}
              className={[s.no, ativo ? s.noOn : '', apagado(item.no.id, item.no) ? s.fade : '']
                .filter(Boolean)
                .join(' ')}
              data-testid={`no-${item.no.id}`}
              data-tipo={item.no.kind}
              data-subtipo={item.no.subtype ?? undefined}
              data-rotulo={item.no.label}
              // Também é gancho de CSS: em `[data-selecionado="true"]:focus-visible`
              // o anel do disco faz o papel do foco e o `outline` global sai —
              // sem seleção o contorno de teclado continua de pé.
              data-selecionado={ativo ? 'true' : undefined}
              role="button"
              tabIndex={0}
              aria-pressed={ativo}
              aria-label={t('accessMap.graph.nodeLabel', {
                label: item.no.label,
                kind: typeLabel(kindDoNo),
              })}
              transform={`translate(${item.x} ${item.y})`}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                // Espaço rolaria a página; a seleção é a ação do nó.
                event.preventDefault();
                onSelecionar(ativo ? null : item.no.id);
              }}
              onFocus={() => setHover(item.no.id)}
              onBlur={() => setHover((current) => (current === item.no.id ? null : current))}
              onMouseDown={(event) => {
                event.stopPropagation();
                arrasto.current = {
                  tipo: 'no',
                  id: item.no.id,
                  x: event.clientX,
                  y: event.clientY,
                  origem: { x: item.x, y: item.y },
                };
              }}
              onMouseEnter={() => setHover(item.no.id)}
              onMouseLeave={() => setHover((current) => (current === item.no.id ? null : current))}
              onClick={(event) => {
                event.stopPropagation();
                // Idem: no duplo-clique o segundo `click` não alterna a
                // seleção — o gesto é zoom (Bug 3b).
                if (event.detail > 1) return;
                onSelecionar(ativo ? null : item.no.id);
              }}
              onDoubleClick={() => {
                // O evento **sobe** de propósito: quem aproxima é o SVG. Aqui
                // só se reafirma o nó, para o gesto nunca terminar sem
                // seleção (o primeiro `click` desmarca quando ele já estava
                // selecionado).
                onSelecionar(item.no.id);
              }}
            >
              <title>{item.no.label}</title>
              {/* Disco da cor do tipo com o ícone dentro e os rótulos por
                  baixo — a forma do protótipo (`.amn`). O `<use>` aponta para o
                  sprite público, como o componente `Icon`; a tinta do ícone é
                  do tema (`--am-no-tinta`), porque o disco inverte no escuro. */}
              <circle className={s.noDisco} r={NODE_R} style={{ fill: tipo.cor }} />
              <svg
                x={-12}
                y={-12}
                width={24}
                height={24}
                viewBox="0 0 24 24"
                className={s.noIcone}
                aria-hidden="true"
              >
                <use href={`${SPRITE_URL}#i-${tipo.icone}`} />
              </svg>
              <text className={s.noRotulo} y={LABEL_DY} textAnchor="middle">
                {truncar(item.no.label, LABEL_LIMIT)}
              </text>
              <text className={s.noSub} y={SUB_DY} textAnchor="middle">
                {truncar(inSubtitle(item.no), LABEL_LIMIT)}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}
